import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async ({ _timestamp } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/cart');
      return { ...response.data.cart, _timestamp: _timestamp ?? Date.now() };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch cart' });
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async ({ product_id, quantity, unit_price, job_no, ...otherData }, { rejectWithValue, dispatch }) => {
    try {
      // Determine warehouse_id from localStorage selections
      let warehouseId = null;
      try {
        const viewWh = localStorage.getItem('view_warehouse');
        const guestWh = localStorage.getItem('guest_warehouse');
        const parsed = viewWh ? JSON.parse(viewWh) : (guestWh ? JSON.parse(guestWh) : null);
        if (parsed && parsed.id) warehouseId = parsed.id;
      } catch {}

      const payload = { 
        product_id, 
        quantity,
        ...(unit_price !== null && unit_price !== undefined ? { unit_price } : {}), // Include unit_price if provided (including 0)
        ...(warehouseId && { warehouse_id: warehouseId }),
        ...(job_no && { job_no }),
        ...otherData // Include any other data like quote_id, from_quote, etc.
      };
      
      console.log('[CartSlice] Adding to cart with payload:', payload);
      
      const response = await api.post('/cart', payload);
      // Refresh cart after adding item
      dispatch(fetchCart());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to add to cart' });
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async ({ product_id, quantity, job_no }, { rejectWithValue, dispatch }) => {
    try {
      const payload = {};
      if (quantity !== undefined) payload.quantity = quantity;
      if (job_no !== undefined) payload.job_no = job_no;
      const response = await api.put(`/cart/${product_id}`, payload);
      // Refresh cart after updating item
      dispatch(fetchCart());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update cart item' });
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (product_id, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/cart/${product_id}`);
      // Refresh cart after removing item
      dispatch(fetchCart());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to remove from cart' });
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.delete('/cart');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to clear cart' });
    }
  }
);

export const mergeGuestCart = createAsyncThunk(
  'cart/mergeGuestCart',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const items = state?.cart?.items || [];
    const guestItems = items.filter(item => {
      if (!item) return false;
      const id = String(item.id ?? '');
      return id.startsWith('local_');
    });

    if (guestItems.length === 0) {
      return { mergedCount: 0, failed: [] };
    }

    const failed = [];

    let warehouseId = null;
    try {
      const viewWh = localStorage.getItem('view_warehouse');
      const guestWh = localStorage.getItem('guest_warehouse');
      const parsed = viewWh ? JSON.parse(viewWh) : (guestWh ? JSON.parse(guestWh) : null);
      if (parsed && parsed.id) {
        warehouseId = parsed.id;
      }
    } catch (err) {
      console.warn('[Cart][mergeGuestCart] Failed to parse warehouse selection', err);
    }

    for (const item of guestItems) {
      if (!item?.product?.id) {
        failed.push({ product_id: null, reason: 'Missing product information' });
        continue;
      }

      const isQuotedItem = !!item.quote_id || !!item.from_quote;
      const payload = {
        product_id: item.product.id,
        quantity: item.quantity || 1,
        // Only forward unit_price for quoted items — for regular items let the backend
        // resolve the authenticated customer's price so public prices are never carried over.
        ...(isQuotedItem && item.unit_price !== null && item.unit_price !== undefined ? { unit_price: item.unit_price } : {}),
        ...(item.quote_id ? { quote_id: item.quote_id } : {}),
        ...(item.from_quote ? { from_quote: true } : {}),
        ...(warehouseId ? { warehouse_id: warehouseId } : {})
      };

      try {
        await api.post('/cart', payload);
      } catch (error) {
        failed.push({
          product_id: item.product.id,
          reason: error?.response?.data || error.message || 'Unknown error'
        });
      }
    }

    dispatch({ type: 'cart/clearCartLocal' });

    const mergedCount = guestItems.length - failed.length;
    return { mergedCount, failed };
  }
);

// Initial state
const initialState = {
  items: [],
  subtotal: 0,    // GST-inclusive subtotal
  tax_amount: 0,  // Always 0 since GST is included in the price
  tax_rate: 0,    // 0% since GST is already included
  total: 0,       // Same as subtotal for GST-inclusive pricing
  item_count: 0,
  loading: false,
  error: null,
  isOpen: false,  // For cart sidebar/modal
  lastFetchTimestamp: 0,
};

// Cart slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    toggleCart: (state) => {
      state.isOpen = !state.isOpen;
    },
    openCart: (state) => {
      state.isOpen = true;
    },
    closeCart: (state) => {
      state.isOpen = false;
    },
    // Local cart management for guest users
    addItemLocal: (state, action) => {
      const { product, quantity } = action.payload;
      const existingItem = state.items.find(item => item.product.id === product.id);
      
      if (existingItem) {
        existingItem.quantity += quantity;
        existingItem.line_total = existingItem.quantity * existingItem.unit_price;
      } else {
        state.items.push({
          id: `local_${product.id}_${Date.now()}`,
          product,
          quantity,
          unit_price: product.effective_price ?? product.list_price ?? null,
          list_price: product.list_price,
          line_total: quantity * (product.effective_price ?? product.list_price ?? 0),
          added_at: new Date().toISOString()
        });
      }
      
      // Recalculate totals
      state.subtotal = state.items.reduce((sum, item) => sum + item.line_total, 0);
      state.tax_amount = state.subtotal * state.tax_rate;
      state.total = state.subtotal + state.tax_amount;
      state.item_count = state.items.reduce((sum, item) => sum + item.quantity, 0);
    },
    updateItemLocal: (state, action) => {
      const { product_id, quantity } = action.payload;
      const item = state.items.find(item => item.product.id === product_id);
      
      if (item) {
        item.quantity = quantity;
        item.line_total = item.quantity * item.unit_price;
        
        // Recalculate totals
        state.subtotal = state.items.reduce((sum, item) => sum + item.line_total, 0);
        state.tax_amount = state.subtotal * state.tax_rate;
        state.total = state.subtotal + state.tax_amount;
        state.item_count = state.items.reduce((sum, item) => sum + item.quantity, 0);
      }
    },
    removeItemLocal: (state, action) => {
      const product_id = action.payload;
      state.items = state.items.filter(item => item.product.id !== product_id);
      
      // Recalculate totals
      state.subtotal = state.items.reduce((sum, item) => sum + item.line_total, 0);
      state.tax_amount = state.subtotal * state.tax_rate;
      state.total = state.subtotal + state.tax_amount;
      state.item_count = state.items.reduce((sum, item) => sum + item.quantity, 0);
    },
    clearCartLocal: (state) => {
      state.items = [];
      state.subtotal = 0;
      state.tax_amount = 0;
      state.total = 0;
      state.item_count = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        const payloadTs = action.payload._timestamp || 0;
        // Ignore stale responses so an early pre-login fetchCart cannot
        // overwrite the merged-cart result that arrives later.
        if (payloadTs < state.lastFetchTimestamp) {
          state.loading = false;
          return;
        }
        state.loading = false;
        state.items = action.payload.items;
        state.subtotal = action.payload.subtotal;
        state.tax_amount = action.payload.tax_amount;
        state.tax_rate = action.payload.tax_rate || 0.10;
        state.total = action.payload.total;
        state.item_count = action.payload.item_count;
        state.error = null;
        state.lastFetchTimestamp = payloadTs;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add to cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update cart item
      .addCase(updateCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Remove from cart
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Clear cart
      .addCase(clearCart.fulfilled, (state) => {
        state.items = [];
        state.subtotal = 0;
        state.tax_amount = 0;
        state.total = 0;
        state.item_count = 0;
      })
      .addCase(mergeGuestCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mergeGuestCart.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(mergeGuestCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error;
      });
  },
});

export const {
  clearError,
  toggleCart,
  openCart,
  closeCart,
  addItemLocal,
  updateItemLocal,
  removeItemLocal,
  clearCartLocal,
} = cartSlice.actions;

// Selectors
export const selectCart = (state) => state.cart;
export const selectCartItems = (state) => state.cart.items;
export const selectCartTotal = (state) => state.cart.total;
export const selectCartItemCount = (state) => state.cart.item_count;
export const selectCartLoading = (state) => state.cart.loading;
export const selectCartError = (state) => state.cart.error;
export const selectCartIsOpen = (state) => state.cart.isOpen;

export default cartSlice.reducer;
